/* eslint-disable @direct-frontend/modules/no-restricted-entity-imports -- модалка маппинга интеграций */
import { type FC, type ReactNode, useCallback, useState } from 'react';
import { Button, Header, Island, OverlayContent, type TestProps, VStack } from '@direct-frontend/components';
import { IntegrationMappingEditor } from '#shared/components/IntegrationMappingEditor';
import { type INTEGRATION_TYPE_1C, type TagWithScope } from '#shared/lib/integrations';
import { showError, showSuccess } from '#shared/lib/toast';
import { Modal, type ModalProps } from '#shared/ui/Modal';
import { useIntegrationMappingAvailableItems } from './hooks/useIntegrationMappingAvailableItems';
import { useIntegrationMappingTags } from './hooks/useMappingTags';
import { getIntegrationMappingTagsKey } from './lib/getIntegrationMappingTagsKey';
import { mapIntegrationSelectedTagsToMappingPayload } from './lib/mapSelectedTagsToMappingPayload';
import { setIntegrationMappingSettings, useGetIntegrationMappingSettings } from '../../api';
import { type AvailableSource as AvailableIntegrationSource } from '../../lib/integrationSourceData';

const DEFAULT_SAVE_SUCCESS_MESSAGE = 'Параметры сопоставления сохранены';
const DEFAULT_SAVE_ERROR_LABEL = 'Не удалось сохранить параметры сопоставления';

export type IntegrationMappingModalProps = {
    isOpen: boolean;
    onClose: VoidFunction;
    source: AvailableIntegrationSource | typeof INTEGRATION_TYPE_1C;
    title: string;
    caption?: string;
    beforeEditor?: ReactNode;
    label?: string;
    afterEditor?: ReactNode;
    onSave?: (selectedTags: TagWithScope[]) => void | Promise<void>;
    onSaved?: VoidFunction;
    showSuccessToast?: boolean | string;
    saveErrorLabel?: string;
    isSaving?: boolean;
    size?: ModalProps['size'];
    saveLabel?: string;
    cancelLabel?: string;
    isExistingMappingReadOnly?: boolean;
} & TestProps;

type IntegrationMappingModalContentProps = {
    initialTags: TagWithScope[];
    onClose: VoidFunction;
    source: AvailableIntegrationSource | typeof INTEGRATION_TYPE_1C;
    title: string;
    caption?: string;
    beforeEditor?: ReactNode;
    label?: string;
    afterEditor?: ReactNode;
    onSave?: (selectedTags: TagWithScope[]) => void | Promise<void>;
    onSaved?: VoidFunction;
    showSuccessToast?: boolean | string;
    saveErrorLabel?: string;
    isSavingExternal: boolean;
    isSavingInternal: boolean;
    setIsSavingInternal: (isSaving: boolean) => void;
    saveLabel: string;
    cancelLabel: string;
    isReadOnly: boolean;
    dataTestId: string;
};

const IntegrationMappingModalContent: FC<IntegrationMappingModalContentProps> = ({
    initialTags,
    onClose,
    source,
    title,
    caption,
    beforeEditor,
    label,
    afterEditor,
    onSave,
    onSaved,
    showSuccessToast = false,
    saveErrorLabel = DEFAULT_SAVE_ERROR_LABEL,
    isSavingExternal,
    isSavingInternal,
    setIsSavingInternal,
    saveLabel,
    cancelLabel,
    isReadOnly,
    dataTestId,
}) => {
    const [selectedTags, setSelectedTags] = useState(initialTags);
    const { availableItems, isFetching, isError, onRetry } = useIntegrationMappingAvailableItems({ selectedTags });

    const isSaving = onSave ? isSavingExternal : isSavingInternal;

    const handleSaveClick = useCallback(async () => {
        if (onSave) {
            await onSave(selectedTags);

            return;
        }

        setIsSavingInternal(true);

        try {
            await setIntegrationMappingSettings({
                params: { integration_type: source },
                payload: mapIntegrationSelectedTagsToMappingPayload(selectedTags),
            });

            if (showSuccessToast) {
                showSuccess(typeof showSuccessToast === 'string' ? showSuccessToast : DEFAULT_SAVE_SUCCESS_MESSAGE);
            }

            onSaved?.();
            onClose();
        } catch (error) {
            showError({ label: saveErrorLabel }, error);
        } finally {
            setIsSavingInternal(false);
        }
    }, [onClose, onSave, onSaved, saveErrorLabel, selectedTags, setIsSavingInternal, showSuccessToast, source]);

    return (
        <OverlayContent
            bottomButtons={
                <>
                    <Button
                        color="accent"
                        isLoading={isSaving}
                        size="m"
                        onClick={handleSaveClick}
                        data-testid={`${dataTestId}.SaveButton`}
                    >
                        {saveLabel}
                    </Button>
                    <Button
                        color="contrast"
                        size="m"
                        isDisabled={isSaving}
                        onClick={onClose}
                        data-testid={`${dataTestId}.CancelButton`}
                    >
                        {cancelLabel}
                    </Button>
                </>
            }
        >
            <Island>
                <VStack gap="28">
                    <Header level="h2" caption={caption} data-testid={`${dataTestId}.Header`}>
                        {title}
                    </Header>
                    {beforeEditor}
                    <IntegrationMappingEditor
                        selectedTags={selectedTags}
                        setSelectedTags={setSelectedTags}
                        availableItems={availableItems}
                        label={label}
                        isFetching={isFetching}
                        isError={isError}
                        onRetry={onRetry}
                        isReadOnly={isReadOnly}
                        data-testid={`${dataTestId}.Editor`}
                    />
                    {afterEditor}
                </VStack>
            </Island>
        </OverlayContent>
    );
};

export const IntegrationMappingModal: FC<IntegrationMappingModalProps> = ({
    isOpen,
    onClose,
    source,
    title,
    caption,
    beforeEditor,
    label,
    afterEditor,
    onSave,
    onSaved,
    showSuccessToast = false,
    saveErrorLabel = DEFAULT_SAVE_ERROR_LABEL,
    isSaving: isSavingExternal = false,
    size,
    saveLabel = 'Сохранить',
    cancelLabel = 'Отмена',
    isExistingMappingReadOnly = false,
    'data-testid': dataTestId = 'IntegrationMappingModal',
}) => {
    const [isSavingInternal, setIsSavingInternal] = useState(false);
    const { data: mapping } = useGetIntegrationMappingSettings({ integration_type: source }, { skip: !isOpen });
    const initialTags = useIntegrationMappingTags({ mapping });

    const isSaving = onSave ? isSavingExternal : isSavingInternal;
    const isModalOpen = isOpen || isSaving;

    return (
        <Modal isOpen={isModalOpen} onClose={isSaving ? undefined : onClose} size={size} data-testid={dataTestId}>
            {isModalOpen ? (
                <IntegrationMappingModalContent
                    key={getIntegrationMappingTagsKey(initialTags)}
                    initialTags={initialTags}
                    onClose={onClose}
                    source={source}
                    title={title}
                    caption={caption}
                    beforeEditor={beforeEditor}
                    label={label}
                    afterEditor={afterEditor}
                    onSave={onSave}
                    onSaved={onSaved}
                    showSuccessToast={showSuccessToast}
                    saveErrorLabel={saveErrorLabel}
                    isSavingExternal={isSavingExternal}
                    isSavingInternal={isSavingInternal}
                    setIsSavingInternal={setIsSavingInternal}
                    saveLabel={saveLabel}
                    cancelLabel={cancelLabel}
                    isReadOnly={isExistingMappingReadOnly && initialTags.length > 0}
                    dataTestId={dataTestId}
                />
            ) : null}
        </Modal>
    );
};
